import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import { Vector3, type Sprite } from "three";
import { STATUS_COLOR, SELECTED_COLOR, type DeviceType, type DeviceStatus } from "./device";
import { createIconTexture, createLabelTexture } from "./marker-texture";

type LodTier = "near" | "mid" | "far";

// 거리 임계값 (m). 모델 약 200m 폭 기준.
const NEAR_THRESHOLD = 100;
const FAR_THRESHOLD = 250;

// sprite는 sizeAttenuation(원근 축소)이 켜져 있으므로, scale을 거리에 비례시키면
// 화면상 크기가 거의 일정해진다 (screen px ∝ K). K는 경험값 — 추후 튜닝.
const SCREEN_K = 0.04;
const TIER_FACTOR: Record<LodTier, number> = { near: 1, mid: 0.8, far: 0.55 };

interface DeviceMarkerProps {
  position: [number, number, number];
  type: DeviceType;
  name: string;
  status: DeviceStatus;
  /** 라벨 표시 여부 (near tier에서만 실제 표시) */
  showLabel?: boolean;
  /** 선택 상태 — 하이라이트 + 항상 위에 표시(가림 무시) */
  selected?: boolean;
  /** 클릭 핸들러 */
  onClick?: () => void;
}

/**
 * 인캔버스 디바이스 마커 (sprite + CanvasTexture).
 * - depthTest로 건물에 자연 가림 (외관/X-Ray 모드는 건물이 반투명이라 내부 마커가 비침)
 * - 선택 시 depthTest off + renderOrder ↑ 로 항상 보임
 * - LOD: near 풀마커+라벨 / mid 마커 / far 작은 마커
 */
export function DeviceMarker({
  position,
  type,
  name,
  status,
  showLabel = false,
  selected = false,
  onClick,
}: DeviceMarkerProps) {
  const iconRef = useRef<Sprite>(null);
  const labelRef = useRef<Sprite>(null);
  const [tier, setTier] = useState<LodTier>("mid");
  const { camera } = useThree();
  const target = useMemo(() => new Vector3(...position), [position]);

  const labelOn = showLabel && tier === "near";
  const iconTex = useMemo(
    () => createIconTexture(type, status.level, selected),
    [type, status.level, selected]
  );
  const labelData = useMemo(
    () => (labelOn ? createLabelTexture(status.meta ? `${name} · ${status.meta}` : name) : null),
    [labelOn, name, status.meta]
  );

  // GPU 텍스처 누수 방지 — 텍스처 교체/언마운트 시 dispose
  useEffect(() => () => iconTex.dispose(), [iconTex]);
  useEffect(() => {
    const t = labelData?.texture;
    return () => t?.dispose();
  }, [labelData]);

  useFrame(() => {
    const dist = camera.position.distanceTo(target);
    const nt: LodTier = dist < NEAR_THRESHOLD ? "near" : dist < FAR_THRESHOLD ? "mid" : "far";
    if (nt !== tier) setTier(nt);

    const h = SCREEN_K * dist * TIER_FACTOR[tier];
    iconRef.current?.scale.set(h, h, 1);
    if (labelRef.current && labelData) {
      const lh = h * 0.5;
      labelRef.current.scale.set(lh * labelData.aspect, lh, 1);
      labelRef.current.position.set(0, h * 0.85, 0); // 아이콘 위
    }
  });

  // Stem: 지면까지 점선 + 지면 ring (1m 이상, far tier 제외)
  const stemHeight = position[1];
  const showStem = stemHeight > 1 && tier !== "far";
  const ringColor = selected ? SELECTED_COLOR : STATUS_COLOR[status.level];

  return (
    <group position={position}>
      {showStem && (
        <>
          <Line
            points={[
              [0, 0, 0],
              [0, -stemHeight, 0],
            ]}
            color={ringColor}
            lineWidth={1.5}
            dashed
            dashScale={3}
            dashSize={0.5}
            gapSize={0.3}
            transparent
            opacity={0.85}
            depthWrite={false}
          />
          <mesh position={[0, -stemHeight, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.6, 1.2, 24]} />
            <meshBasicMaterial color={ringColor} transparent opacity={0.7} depthWrite={false} />
          </mesh>
        </>
      )}

      <sprite
        ref={iconRef}
        renderOrder={selected ? 10 : 0}
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation();
                onClick();
              }
            : undefined
        }
      >
        <spriteMaterial map={iconTex} transparent depthTest={!selected} depthWrite={false} />
      </sprite>

      {labelData && (
        <sprite ref={labelRef} renderOrder={selected ? 10 : 1}>
          <spriteMaterial
            map={labelData.texture}
            transparent
            depthTest={!selected}
            depthWrite={false}
          />
        </sprite>
      )}
    </group>
  );
}
